import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { GetModuleStopPeriods, CreateModuleStopPeriod, UpdateModuleStopPeriod, DeleteModuleStopPeriod } from '../../../store/ModuleStopPeriodAction';
import TableHorizontal from "../../misc/TableHorizontal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import StatusCode from "../../../util/StatusCode";
import { CoreStopPeriod } from "../../../models/CoreModels";
import { Button, Form, FormGroup, Label, FormFeedback, Input } from 'reactstrap';
import ModalComponent from "../../misc/ModalComponent";
import ModulePeriodDateMonthPicker from "./ModulePeriodDateMonthPicker";
import { useTranslation } from "react-i18next";
import BoolInput from "../../misc/BoolInput";
import { RequestState } from "../../../models/RequestState";
import { InfoObject } from "../../misc/TableHorizontal";
import { RootState } from "../../../store/store";
import { FlowSyncAccessPolicy, isAuthorized } from '../../../models/FlowSyncAccessPolicy';

const ActionName = {
    NEW: 'NEW',
    EDIT: 'EDIT',
    DELETE: 'DELETE'
};

interface StopPeriodProps {
    moduleId: string
}

const validate = (period: Partial<CoreStopPeriod> | undefined, validateNew: boolean): string[] => {
    const validation = [];

    if (period === undefined) {
        return [];
    }

    if (period.reason === "") {
        validation.push("reason");
    }

    if (validateNew) {
        if (!period.reason) {
            validation.push("reason");
        }
        if (!period.startDayOfMonth) {
            validation.push("startDayOfMonth");
        }
        if (!period.startMonth) {
            validation.push("startMonth");
        }
        if (!period.stopDayOfMonth) {
            validation.push("stopDayOfMonth");
        }
        if (!period.stopMonth) {
            validation.push("stopMonth");
        }
    }

    return validation;
}

const StopPeriods = (props: StopPeriodProps) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [name, setName] = useState<string | undefined>(undefined);
    const [period, setPeriod] = useState<Partial<CoreStopPeriod> | undefined>(undefined);
    const [saveStatus, setSaveStatus] = useState<StatusCode>(StatusCode.NONE);
    const [validateNew, setValidateNew] = useState<boolean>(false);
    const validation = validate(period, validateNew);
    const moduleStopPeriods = useSelector<RootState, RequestState<CoreStopPeriod[]>>(state => state.moduleStopPeriod.moduleStopPeriods);

    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const editModuleAccess = useMemo(() => {
        return isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_MODULE);
    }, [authorizedAccessPolicies])

    const { t } = useTranslation(['configuration', 'common']);

    useEffect(() => {
        GetModuleStopPeriods(props.moduleId)(dispatch);
    }, [dispatch, props.moduleId]);

    useEffect(() => {
        if (saveStatus === StatusCode.COMPLETE) {
            setTimeout(() => {
                setSaveStatus(StatusCode.NONE);
                toggleModal();
                GetModuleStopPeriods(props.moduleId)(dispatch);
            }, 1500);
        }
    }, [saveStatus]);

    const update = async () => {
        setValidateNew(true);
        let updateValidation = validate(period, true)

        if (updateValidation.length !== 0) {
            return;
        }

        if (period === undefined) {
            return;
        }

        setSaveStatus(StatusCode.PENDING);
        if (name === ActionName.NEW) {
            const status = await CreateModuleStopPeriod(props.moduleId, period)(dispatch);
            setSaveStatus(status);
        }
        else if (name === ActionName.EDIT) {
            const status = await UpdateModuleStopPeriod(props.moduleId, period)(dispatch);
            setSaveStatus(status);
        } else if (name === ActionName.DELETE) {
            if (period?.id !== undefined) {
                const status = await DeleteModuleStopPeriod(props.moduleId, period.id)(dispatch);
                setSaveStatus(status);
            }
        }
    };

    const toggleModal = () => {
        setIsOpen(!isOpen);
        setName(undefined);
        setPeriod(undefined);
        setSaveStatus(StatusCode.NONE);
        setValidateNew(false);
    };

    const stopPeriods = () => {
        let stopPeriodInfo: InfoObject[] = [];
        if (moduleStopPeriods.code === StatusCode.COMPLETE) {
            stopPeriodInfo = moduleStopPeriods.data.map(x => ({
                id: x.id,
                data: [
                    { value: `${x.startDayOfMonth} ${t(`months.${x.startMonth}`, { ns: 'common' })}` },
                    { value: `${x.stopDayOfMonth} ${t(`months.${x.stopMonth}`, { ns: 'common' })}` },
                    { value: x.reason },
                    { value: t(x.preventNextModule ? 'yes' : 'no', { ns: 'common' }) },
                    { value: <Button className="btn-icon" disabled={!editModuleAccess} onClick={() => { setName(ActionName.EDIT); setIsOpen(true); setPeriod(x); }}><FontAwesomeIcon icon="pen" title={t('stopPeriod.editTitle')} /></Button> },
                    { value: <Button className="btn-icon" disabled={!editModuleAccess} onClick={() => { setName(ActionName.DELETE); setIsOpen(true); setPeriod(x); }}><FontAwesomeIcon icon="trash" title={t('stopPeriod.editTitle')} /></Button> },
                ]
            })) ?? [];
        }

        return <>
            <TableHorizontal
                header={[{ value: t('period.startDate') }, { value: t('period.stopDate') }, { value: t('stopPeriod.reason') }, { value: t('stopPeriod.preventNextModule') }, { value: "", className: "w20px" }, { value: "", className: "w20px" }]}
                info={stopPeriodInfo}
                error={moduleStopPeriods.code === StatusCode.ERROR}
                searching={moduleStopPeriods.code === StatusCode.PENDING}
                showHeader={true}
                noResMsg={t('stopPeriod.noResult')}
            />
            <Button color="primary" disabled={!editModuleAccess} onClick={() => { setIsOpen(true); setName(ActionName.NEW); setPeriod({}); }}>{t('stopPeriod.create')}</Button>
        </>
    };

    const onChangePeriod = (name: string, value: number | undefined) => {
        let updatedPeriod = { [name]: value };
        if (name === 'startMonth') {
            updatedPeriod.startDayOfMonth = undefined;
        }
        if (name === 'stopMonth') {
            updatedPeriod.stopDayOfMonth = undefined;
        }
        setPeriod({ ...period, ...updatedPeriod })
    }

    const stopPeriodModal = () => {
        const actionI18nKey = name === ActionName.NEW ? "create" : name === ActionName.EDIT ? "edit" : name === ActionName.DELETE ? "remove" : "";

        const actionName = t(`action.${actionI18nKey}`, { ns: 'common' });

        return (
            <ModalComponent
                isOpen={isOpen}
                toggleModal={toggleModal}
                header={actionName + " " + t('stopPeriod.stopPeriod')}
                cancel={t('action.cancel', { ns: 'common' })}
                confirm={actionName}
                saveStatus={saveStatus}
                update={() => update()}
                disabled={!editModuleAccess || validation.length > 0}
            >
                <Form>
                    <FormGroup>
                        <Label>{t('period.startDate')}</Label>
                        <ModulePeriodDateMonthPicker
                            month={period?.startMonth}
                            date={period?.startDayOfMonth}
                            monthName="startMonth"
                            dateName="startDayOfMonth"
                            disabled={name === ActionName.DELETE || !editModuleAccess}
                            onChangePeriod={(name, value) => onChangePeriod(name, value)}
                            validation={validation}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>{t('period.stopDate')}</Label>
                        <ModulePeriodDateMonthPicker
                            month={period?.stopMonth}
                            date={period?.stopDayOfMonth}
                            monthName="stopMonth"
                            dateName="stopDayOfMonth"
                            disabled={name === ActionName.DELETE || !editModuleAccess}
                            onChangePeriod={(name, value) => onChangePeriod(name, value)}
                            validation={validation}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="reason">{t('stopPeriod.reason')}</Label>
                        <Input
                            type="text"
                            name="reason"
                            id="reason"
                            placeholder={t('stopPeriod.reason')}
                            autoComplete="off"
                            value={period?.reason || ""}
                            disabled={name === ActionName.DELETE || !editModuleAccess}
                            onChange={e => setPeriod({ ...period, "reason": e.target.value })}
                            invalid={validation.indexOf("reason") !== -1}
                        />
                        <FormFeedback>{t('stopPeriod.reasonRequired')}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <BoolInput
                            title={t('stopPeriod.preventNextModule')}
                            titleSmall={true}
                            name="preventNextModule"
                            value={period?.preventNextModule ?? false}
                            disabled={name === ActionName.DELETE || !editModuleAccess}
                            updateValue={(_, v) => setPeriod({ ...period, preventNextModule: v })}
                            invalid={validation.indexOf("reason") !== -1}
                        />
                        <FormFeedback>{t('stopPeriod.reasonRequired')}</FormFeedback>
                    </FormGroup>
                </Form>
            </ModalComponent>
        )
    }

    return (
        <div className="mt-4">
            <h5>{t('stopPeriod.heading')}</h5>
            <p>{t('stopPeriod.ingress')}</p>
            {stopPeriods()}
            {isOpen && period && stopPeriodModal()}
        </div>
    );
}

export default StopPeriods;
